import { Container, Grid, Spinner, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikDateRangePicker, FormikSelect, FormikSelectAsyncPaginate } from 'components/Formik';
import Title from 'components/Title';
import { fetchPaginatedEmployee } from 'hooks/employee/useFetchPaginatedEmployee';
import { useDownloadGPSCoordinatesCSV } from 'hooks/reports/useDownloadGPSCoordinatesCSV';
import { useDownloadJobsCSV } from 'hooks/reports/useDownloadJobsCSV';
import { useDownloadUsersCSV } from 'hooks/reports/useDownloadUsersCSV';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import {
  dataExportsValidationSchema,
  initialDataExportsFormData,
  initialJobDataExportsFormData,
  initialUserDataExportsFormData,
  jobDataExportsValidationSchema,
  userDataExportsValidationSchema,
} from './schema';

const DataExports: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'reports']);

  const formikRef = useRef<any>();

  const { mutateAsync: mutateAsyncDownloadGPSCoordinatesCSV, isLoading: isDownloadGPSCoordinatesCSVLoading } =
    useDownloadGPSCoordinatesCSV();

  const { mutateAsync: mutateAsyncDownloadJobsCSV, isLoading: isDownloadJobsCSVLoading } = useDownloadJobsCSV();

  const { mutateAsync: mutateAsyncDownloadUsersCSV, isLoading: isDownloadUsersCSVLoading } = useDownloadUsersCSV();

  const handleFormSubmit = (values: typeof initialDataExportsFormData) => {
    const postData = {
      user_id: values.user_id ? values.user_id?.userId : '',
      start_date: values.date_range[0] !== null ? format(values.date_range[0], 'MM-dd-yyyy') : '',
      end_date: values.date_range[1] !== null ? format(values.date_range[1], 'MM-dd-yyyy') : '',
    };

    mutateAsyncDownloadGPSCoordinatesCSV(postData);
  };

  const handleJobFormSubmit = (values: typeof initialJobDataExportsFormData) => {
    const postData = {
      job_status: values.job_status ? values.job_status.value : null,
    };

    mutateAsyncDownloadJobsCSV(postData);
  };

  const handleUserFormSubmit = (values: typeof initialUserDataExportsFormData) => {
    const postData = {
      active: values.active ? values.active.value : null,
    };

    mutateAsyncDownloadUsersCSV(postData);
  };

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedEmployee, { page, search, billing_rate_type: null, active: '1' });
  };

  const statusOptions = [
    {
      label: 'All',
      value: null,
    },
    {
      label: 'Active',
      value: true,
    },
    {
      label: 'Inactive',
      value: false,
    },
  ];

  return (
    <div>
      <Title>{t('reports:dataExports')}</Title>
      <div>
        <Container mt="10" maxW="container.xl" p="0">
          <Formik
            initialValues={initialDataExportsFormData}
            validationSchema={dataExportsValidationSchema}
            onSubmit={handleFormSubmit}
          >
            {formikBag => {
              formikRef.current = formikBag;

              return (
                <Form autoComplete="off">
                  <Grid
                    templateColumns={{
                      base: '1fr',
                      sm: 'repeat(2, 1fr)',
                      lg: 'repeat(3, 1fr)',
                      xl: '1fr 1fr 1fr auto',
                    }}
                    mt="3"
                    gap="4"
                    alignContent="flex-start"
                  >
                    <Text fontSize={18} fontWeight="semibold">
                      {t('reports:downloadGPSCoordinates')}:
                    </Text>
                    <FormikSelectAsyncPaginate
                      label=""
                      name="user_id"
                      loadOptions={employeeLoadOptions}
                      selectProps={{
                        isMulti: false,
                        placeholder: t('common:selectUser'),
                        getOptionLabel: (option: any) => option?.firstName + ' ' + option?.lastName,
                        getOptionValue: (option: any) => option.userId,
                      }}
                    />
                    <FormikDateRangePicker name="date_range" />

                    <Button
                      type="submit"
                      colorScheme="secondary"
                      isLoading={isDownloadGPSCoordinatesCSVLoading}
                      height="48px"
                    >
                      {isDownloadGPSCoordinatesCSVLoading ? (
                        <>
                          <Spinner mr="2" size="sm" />
                          Loading...
                        </>
                      ) : (
                        t('reports:exportAsCSV')
                      )}
                    </Button>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Container>
        <hr />
        <Container mt="10" maxW="container.xl" p="0">
          <Formik
            initialValues={initialJobDataExportsFormData}
            validationSchema={jobDataExportsValidationSchema}
            onSubmit={handleJobFormSubmit}
          >
            {formikBag => {
              formikRef.current = formikBag;
              return (
                <Form autoComplete="off">
                  <Grid
                    templateColumns={{
                      base: '1fr',
                      sm: 'repeat(2, 1fr)',
                      lg: 'repeat(3, 1fr)',
                      xl: '1fr 2fr auto 0fr',
                    }}
                    mt="3"
                    gap="4"
                    alignContent="flex-start"
                  >
                    <Text fontSize={18} fontWeight="semibold">
                      {t('reports:downloadJobs')}:
                    </Text>
                    <FormikSelect
                      label=""
                      name="job_status"
                      options={statusOptions}
                      selectProps={{
                        isMulti: false,
                        placeholder: t('common:status'),
                      }}
                    />

                    <Button type="submit" colorScheme="secondary" isLoading={isDownloadJobsCSVLoading} height="48px">
                      {isDownloadJobsCSVLoading ? (
                        <>
                          <Spinner mr="2" size="sm" />
                          Loading...
                        </>
                      ) : (
                        t('reports:exportAsCSV')
                      )}
                    </Button>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Container>
        <hr />
        <Container mt="10" maxW="container.xl" p="0">
          <Formik
            initialValues={initialUserDataExportsFormData}
            validationSchema={userDataExportsValidationSchema}
            onSubmit={handleUserFormSubmit}
          >
            {formikBag => {
              formikRef.current = formikBag;
              return (
                <Form autoComplete="off">
                  <Grid
                    templateColumns={{
                      base: '1fr',
                      sm: 'repeat(2, 1fr)',
                      lg: 'repeat(3, 1fr)',
                      xl: '1fr 2fr auto 0fr',
                    }}
                    mt="3"
                    gap="4"
                    alignContent="flex-start"
                  >
                    <Text fontSize={18} fontWeight="semibold">
                      {t('reports:downloadUsers')}:
                    </Text>
                    <FormikSelect
                      label=""
                      name="active"
                      options={statusOptions}
                      selectProps={{
                        isMulti: false,
                        placeholder: t('common:status'),
                      }}
                    />

                    <Button type="submit" colorScheme="secondary" isLoading={isDownloadUsersCSVLoading} height="48px">
                      {isDownloadUsersCSVLoading ? (
                        <>
                          <Spinner mr="2" size="sm" />
                          Loading...
                        </>
                      ) : (
                        t('reports:exportAsCSV')
                      )}
                    </Button>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Container>
      </div>
    </div>
  );
};

export default DataExports;
